/**
 * Spec 218 PR4 — Invoice review (`/invoices/new/review`).
 *
 * Web port of `mobile/src/screens/InvoiceReviewScreen.tsx`. Reads the
 * draft from `location.state` (passed by EmitInvoice) and lets the user
 * confirm + emit. On success we navigate to /invoices/new/emitted with
 * the EmitResponse so the next screen can show the QR. If the user
 * lands here without a draft (cold reload), redirect to /invoices/new.
 */
import { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft } from 'lucide-react';

import {
  emitInvoice,
  InvoicingError,
  type InvoicingCtx,
} from '../sync/invoicing';
import type { InvoiceDraft } from '@cuadrilla/types/invoice';
import type { WebIdentity } from '../store/keys';

import './InvoiceReview.css';

interface InvoiceReviewProps {
  identity: WebIdentity;
}

interface NavState {
  draft?: InvoiceDraft;
}

function buildCtx(identity: WebIdentity): InvoicingCtx {
  return { companyToken: identity.companyToken };
}

export function InvoiceReview({ identity }: InvoiceReviewProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const navState = (location.state ?? null) as NavState | null;
  const draft = navState?.draft;

  const [confirmed, setConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Block navigation away mid-emit so the user doesn't lose the draft
  // if a slow Verifacti API call is in flight.
  useEffect(() => {
    if (!submitting) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [submitting]);

  if (!draft) {
    return <Navigate to="/invoices/new" replace />;
  }

  const totalBase = draft.lines.reduce((s, l) => s + (Number(l.base) || 0), 0);
  const totalVat = draft.lines.reduce((s, l) => s + (Number(l.vatAmount) || 0), 0);
  const total = totalBase + totalVat;

  async function doEmit() {
    if (!draft) return;
    setSubmitting(true);
    setError(null);
    try {
      const response = await emitInvoice(buildCtx(identity), draft);
      navigate('/invoices/new/emitted', {
        replace: true,
        state: { draft, response },
      });
    } catch (err) {
      const msg =
        err instanceof InvoicingError && err.code === 'NETWORK'
          ? 'Sin conexión. Vuelve a intentarlo cuando recuperes señal.'
          : err instanceof Error
            ? err.message
            : String(err);
      setError(msg);
      setSubmitting(false);
    }
  }

  return (
    <main className="ir-shell" data-testid="invoice-review-screen">
      <header className="ir-header">
        <Link
          to="/invoices/new"
          state={{ draft }}
          className="ir-back"
          data-testid="invoice-review-back"
        >
          <ChevronLeft size={20} strokeWidth={2} aria-hidden="true" />
          Volver
        </Link>
        <h1 className="ir-h1" data-testid="invoice-review-title">
          Revisar antes de emitir
        </h1>
      </header>

      <div className="ir-body">
        <section className="ir-warn" data-testid="invoice-review-warning">
          <AlertTriangle size={24} strokeWidth={2} aria-hidden="true" />
          <div>
            <h2 className="ir-warn-title">Comprueba todos los datos</h2>
            <p className="ir-warn-body">
              Una vez emitida, la factura se registra en la AEAT y no se puede modificar. Solo se puede cancelar
              o rectificar (lo que crea una factura nueva).
            </p>
          </div>
        </section>

        <section className="ir-card">
          <Row label="Serie / Nº" value={`${draft.series} / ${draft.number}`} />
          <Row label="Fecha" value={draft.issueDate} />
          <Row label="Tipo" value={draft.type} />
          <Row label="Descripción" value={draft.description} />
        </section>

        {draft.recipient.kind !== 'none' && (
          <section className="ir-card">
            <h2 className="ir-card-title">Cliente</h2>
            <Row label="Nombre" value={draft.recipient.name ?? ''} />
            {draft.recipient.kind === 'national' && draft.recipient.nif && (
              <Row label="NIF" value={draft.recipient.nif} />
            )}
            {draft.recipient.kind !== 'national' && (
              <Row
                label="Documento"
                value={`${draft.recipient.countryCode ?? ''} ${draft.recipient.foreignIdType ?? ''} ${draft.recipient.foreignId ?? ''}`.trim()}
              />
            )}
            {draft.recipient.email && <Row label="Email" value={draft.recipient.email} />}
          </section>
        )}

        <section className="ir-card">
          <h2 className="ir-card-title">Líneas</h2>
          <ul className="ir-lines">
            {draft.lines.map((l, i) => (
              <li key={i} className="ir-line" data-testid={`invoice-review-line-${i}`}>
                <span className="ir-line-desc">{l.description}</span>
                <span className="ir-line-meta">
                  Base {l.base.toFixed(2)} € · IVA {l.vatRate}% · Cuota {l.vatAmount.toFixed(2)} €
                </span>
              </li>
            ))}
          </ul>
          <div className="ir-totals">
            <div className="ir-total-row">
              <span>Total base</span>
              <strong data-testid="invoice-review-total-base">{totalBase.toFixed(2)} €</strong>
            </div>
            <div className="ir-total-row">
              <span>Total IVA</span>
              <strong data-testid="invoice-review-total-vat">{totalVat.toFixed(2)} €</strong>
            </div>
            <div className="ir-total-row ir-total-row-bold">
              <span>Total</span>
              <strong data-testid="invoice-review-total">{total.toFixed(2)} €</strong>
            </div>
          </div>
        </section>

        <label className="ir-confirm">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="invoice-review-confirm"
          />
          <span>
            He revisado los datos y confirmo que son correctos. Acepto que el envío a la AEAT es definitivo.
          </span>
        </label>

        {error && (
          <p className="ir-error" role="alert" data-testid="invoice-review-error">
            {error}
          </p>
        )}

        <div className="ir-actions">
          <button
            type="button"
            className="ir-primary"
            onClick={doEmit}
            disabled={!confirmed || submitting}
            data-testid="invoice-review-emit"
          >
            {submitting ? 'Emitiendo…' : 'Emitir factura'}
          </button>
        </div>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="ir-row">
      <span className="ir-row-label">{label}</span>
      <span className="ir-row-value">{value || '—'}</span>
    </div>
  );
}
